import CloseIcon from "@mui/icons-material/Close";
import {
  Button,
  FormControl,
  FormGroup,
  FormLabel,
  IconButton,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { type FC, useCallback, useEffect, useRef, useState } from "react";
import { Controller, useFieldArray, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import type { PollWrapperSchema } from "./types";

export const PollOptions: FC = () => {
  const { t } = useTranslation();

  const form = useFormContext<PollWrapperSchema>();

  const {
    control,
    formState: { errors },
  } = form;

  const { fields, insert, remove } = useFieldArray({
    control,
    name: "poll.options",
  });

  // Store refs for each input
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
  const [focusIndex, setFocusIndex] = useState<number | null>(null);

  useEffect(() => {
    if (focusIndex !== null && inputRefs.current[focusIndex]) {
      inputRefs.current[focusIndex]?.focus();
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setFocusIndex(null); // reset to avoid repeated focus
    }
  }, [fields.length, focusIndex]);

  const addOption = useCallback(
    (currentIndex: number) => {
      insert(currentIndex + 1, { text: "" });
      setFocusIndex(currentIndex + 1);
    },
    [insert],
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent, index: number) => {
      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();
        addOption(index);
      }
    },
    [addOption],
  );

  return (
    <FormControl component="fieldset" fullWidth>
      <FormLabel component="legend">
        {t("Options (enter each one on a new line, press Enter to add a new option)")}
      </FormLabel>
      {errors?.poll?.options?.root && (
        <Typography variant="caption" color="error">
          {form.formState.errors.poll?.options?.root?.message}
        </Typography>
      )}

      <FormGroup>
        <Stack spacing={1} sx={{ mt: 1 }}>
          {fields.map((field, index) => (
            <Stack key={field.id} direction="row" spacing={1} alignItems="center">
              <Controller
                name={`poll.options.${index}.text`}
                control={control}
                defaultValue={field.text}
                render={({ field: innerField, fieldState }) => (
                  <TextField
                    {...innerField}
                    inputRef={(ref: HTMLInputElement) => {
                      inputRefs.current[index] = ref;
                    }}
                    onKeyDown={(e) => handleKeyDown(e, index)}
                    fullWidth
                    variant="outlined"
                    placeholder={t("Option")}
                    data-analytics-id="poll-option-input"
                    error={!!fieldState.error}
                    helperText={fieldState.error?.message}
                  />
                )}
              />
              {fields.length > 1 && (
                <IconButton onClick={() => remove(index)} data-analytics-id="remove-poll-option">
                  <CloseIcon />
                </IconButton>
              )}
            </Stack>
          ))}
        </Stack>
      </FormGroup>
      <Button
        onClick={() => addOption(fields.length)}
        variant="text"
        sx={{ mt: 1 }}
        data-analytics-id="add-poll-option"
      >
        {t("Add another option")}
      </Button>
    </FormControl>
  );
};
